import { supabase } from "@/integrations/supabase/client";

export type AdminCompany = {
  id: string;
  name: string;
  slug: string;
  owner_email: string;
  owner_name: string;
  is_platform_owner: boolean;
  status: string;
  subscription_plan_name: string;
  member_count: number;
  website_count: number;
  article_count: number;
  created_at: string;
  last_activity_at: string | null;
  total_count?: number;
};

export type CompaniesDiagnostics = {
  source?: string;
  returned_count?: number;
  total_count?: number;
  filters?: Record<string, unknown>;
  actor_id?: string;
  is_platform_owner?: boolean;
  rls_blocked?: boolean;
  fallback_schema_select?: boolean;
  warning?: string | null;
};

export type CompaniesResponse = {
  companies: AdminCompany[];
  total: number;
  diagnostics?: CompaniesDiagnostics;
};

export type PlatformDashboard = {
  summary: Record<string, number>;
  charts: Record<string, Array<{ month: string; value: number }>>;
  diagnostics?: CompaniesDiagnostics & { companies_scanned?: number };
};

export type CompanyCenter = {
  company: Record<string, any>;
  users: Array<Record<string, any>>;
  invitations: Array<Record<string, any>>;
  subscription: Record<string, any> | null;
  invoices: Array<Record<string, any>>;
  websites: Array<Record<string, any>>;
  articles: Array<Record<string, any>>;
  article_stats: Record<string, number>;
  sources: Array<Record<string, any>>;
  source_stats: Record<string, any>;
  credits: Record<string, any>;
  integrations: Record<string, any>;
  templates: Record<string, Array<Record<string, any>>>;
  logs: Array<Record<string, any>>;
  automations: Array<Record<string, any>>;
  health: { score: number; level: "healthy" | "warning" | "critical"; checks: Array<Record<string, any>> };
  deletion_preview: Record<string, number>;
  diagnostics?: CompaniesDiagnostics;
};

async function getFunctionErrorMessage(error: unknown) {
  const maybeError = error as { message?: string; context?: { json?: () => Promise<unknown>; text?: () => Promise<string> } };
  if (maybeError?.context?.json) {
    try {
      const body = await maybeError.context.json() as { error?: string; message?: string };
      return body.error || body.message || maybeError.message || "Failed to load companies";
    } catch {
      // Fall through to text/message.
    }
  }
  if (maybeError?.context?.text) {
    try {
      const body = await maybeError.context.text();
      if (body) return body;
    } catch {
      // Fall through to message.
    }
  }
  return maybeError?.message || String(error || "Failed to load companies");
}

export async function invokeAdminCompany<T>(body: Record<string, unknown>): Promise<T> {
  const { data: sessionData } = await supabase.auth.getSession();
  let session = sessionData.session;

  if (session?.expires_at && session.expires_at * 1000 - Date.now() < 60_000) {
    const { data: refreshed, error: refreshError } = await supabase.auth.refreshSession();
    if (refreshError) throw new Error(refreshError.message || "Please sign in again.");
    session = refreshed.session;
  }

  if (!session?.access_token) {
    throw new Error("Please sign in again.");
  }

  const { data, error } = await supabase.functions.invoke("admin-company-actions", {
    body,
    headers: { Authorization: `Bearer ${session.access_token}` },
  });
  if (error) throw new Error(await getFunctionErrorMessage(error));
  if (data?.error) throw new Error(data.error);
  return data as T;
}

export async function fetchCompaniesAdmin(search?: string, status?: string, limit = 100, offset = 0): Promise<CompaniesResponse> {
  return invokeAdminCompany<CompaniesResponse>({
    action: "list_companies",
    search: search || null,
    status: status || null,
    limit,
    offset,
  });
}

export async function fetchPlatformDashboard(): Promise<PlatformDashboard> {
  return invokeAdminCompany<PlatformDashboard>({ action: "get_platform_dashboard" });
}

export async function fetchCompanyCenter(companyId: string): Promise<CompanyCenter> {
  const data = await invokeAdminCompany<{ center: CompanyCenter }>({ action: "get_company_center", company_id: companyId });
  return data.center;
}

export async function runCompanyOperation(companyId: string, operation: string, payload: Record<string, unknown> = {}) {
  return invokeAdminCompany<Record<string, any>>({
    action: "company_operation",
    company_id: companyId,
    operation,
    ...payload,
  });
}
